/**
 * Phase 27.50.B.6 — Client order history (`/my-orders`).
 *
 * Lists the requesting user's most recent storefront orders with:
 *   • Status progress (Paid → Shipped → Delivered)
 *   • Download PDF receipt (auth-safe blob fetch → object URL)
 *   • Buy again (POST /api/orders/{id}/reorder → refresh cart badge)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shopAPI } from '@/lib/api';
import { notifyCartUpdated } from '@/hooks/useCartCount';
import { getErrorMessage } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, Package, FileText, RefreshCcw, ShoppingBag, CheckCircle2,
  Truck, PackageCheck,
} from 'lucide-react';

const STATUS_STEPS = [
  { key: 'Paid', label: 'Paid', icon: CheckCircle2 },
  { key: 'Shipped', label: 'Shipped', icon: Truck },
  { key: 'Delivered', label: 'Delivered', icon: PackageCheck },
];

function statusIndex(status) {
  const s = (status || '').toLowerCase();
  if (s.startsWith('deliver')) return 2;
  if (s.startsWith('ship')) return 1;
  if (s.startsWith('paid') || s.startsWith('partially')) return 0;
  return -1;
}

function StatusStepper({ status }) {
  const active = statusIndex(status);
  return (
    <div className="flex items-center gap-2" data-testid="order-status-stepper">
      {STATUS_STEPS.map((step, i) => {
        const done = i <= active;
        const Icon = step.icon;
        return (
          <div key={step.key} className="flex items-center gap-1.5">
            <div
              className={
                'flex items-center justify-center rounded-full h-6 w-6 text-white ' +
                (done ? 'bg-emerald-600' : 'bg-slate-300')
              }
              data-testid={`step-${step.key.toLowerCase()}-${done ? 'done' : 'pending'}`}
            >
              <Icon size={12} />
            </div>
            <span className={`text-xs ${done ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
              {step.label}
            </span>
            {i < STATUS_STEPS.length - 1 && (
              <span className={`h-px w-6 ${i < active ? 'bg-emerald-600' : 'bg-slate-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function MyOrdersPage({ embedded = false }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await shopAPI.listOrders();
      setOrders(r.data.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load orders'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const downloadReceipt = async (orderId) => {
    setBusy(`receipt-${orderId}`);
    try {
      const r = await shopAPI.receiptBlob(orderId);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a short delay so the tab has time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to download receipt'));
    } finally { setBusy(null); }
  };

  const buyAgain = async (orderId) => {
    setBusy(`reorder-${orderId}`);
    try {
      const r = await shopAPI.reorder(orderId);
      notifyCartUpdated();
      const added = r.data.added?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      if (added > 0) {
        toast.success(`${added} item${added === 1 ? '' : 's'} added to cart`, {
          action: { label: 'View cart', onClick: () => nav('/cart') },
        });
      }
      if (skipped > 0) {
        toast.warning(
          `${skipped} item${skipped === 1 ? '' : 's'} skipped (no longer available)`,
        );
      }
      if (added === 0 && skipped === 0) {
        toast.info('Nothing to re-add');
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Reorder failed'));
    } finally { setBusy(null); }
  };

  if (loading) return (
    <div className={`${embedded ? '' : 'max-w-4xl mx-auto p-8'} flex items-center gap-2 text-slate-500`}>
      <Loader2 size={16} className="animate-spin" /> Loading orders…
    </div>
  );

  return (
    <div className={embedded ? '' : 'max-w-4xl mx-auto p-6'} data-testid="my-orders-page">
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package size={22} /> My Orders
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Track your storefront orders, download receipts, and reorder favorites.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => nav('/shop')}
                  data-testid="my-orders-go-shop">
            <ShoppingBag size={14} className="mr-1.5" />
            Keep shopping
          </Button>
        </div>
      )}

      {orders.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-slate-500">
            <Package className="mx-auto mb-3 text-slate-300" size={32} />
            <p>No orders yet.</p>
            <Button className="mt-4" onClick={() => nav('/shop')}>
              Browse products
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const items = o.items || [];
            const itemCount = items.reduce((s, i) => s + (i.quantity || 0), 0);
            const needsPayment = o.is_special_order && o.payment_status !== 'paid'
              && o.payment_status !== 'cancelled' && o.status !== 'Cancelled';
            return (
              <Card key={o.order_id} data-testid={`order-card-${o.order_id}`} className={needsPayment ? 'border-amber-300' : undefined}>
                <CardContent className="p-5 space-y-4">
                  {/* Header row */}
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                        {o.is_special_order ? 'Special Order' : 'Order'}
                      </p>
                      {o.is_special_order && o.title && (
                        <p className="text-sm font-semibold text-[#0a1f47]">{o.title}</p>
                      )}
                      <p className="font-mono text-sm text-slate-800">
                        {o.order_id}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatDate(o.paid_at || o.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                        Total
                      </p>
                      <p className="text-lg font-bold text-slate-900 font-mono">
                        ${Number(o.total || 0).toFixed(2)}
                      </p>
                      <Badge
                        variant="outline"
                        className={`mt-1 text-[10px] ${needsPayment ? 'bg-amber-50 text-amber-800 border-amber-300' : 'bg-emerald-50 text-emerald-800 border-emerald-300'}`}
                        data-testid={`order-status-${o.order_id}`}
                      >
                        {needsPayment ? 'Pending payment' : (o.status || 'Paid')}
                      </Badge>
                    </div>
                  </div>

                  {needsPayment ? (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-center justify-between gap-3" data-testid={`special-pay-banner-${o.order_id}`}>
                      <span>This order is waiting for your payment.</span>
                      <Button size="sm" className="bg-[#0a1f47] hover:bg-[#0a1f47]/90 text-white"
                              onClick={() => nav(`/special-orders/${o.order_id}/pay`)}
                              data-testid={`pay-now-${o.order_id}`}>
                        Pay now
                      </Button>
                    </div>
                  ) : (
                    /* Status stepper */
                    <StatusStepper status={o.status} />
                  )}

                  {/* Items */}
                  <div className="border-t pt-3 space-y-1.5">
                    {items.slice(0, 4).map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700 truncate mr-3">
                          {it.title}
                          <span className="text-slate-400 font-mono ml-1.5">× {it.quantity}</span>
                        </span>
                        <span className="text-slate-500 font-mono flex-shrink-0">
                          ${(Number(it.unit_price || 0) * Number(it.quantity || 0)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {items.length > 4 && (
                      <p className="text-xs text-slate-400 pt-1">
                        + {items.length - 4} more product{items.length - 4 === 1 ? '' : 's'}
                      </p>
                    )}
                    {itemCount > 0 && (
                      <p className="text-xs text-slate-400 pt-1">
                        {itemCount} unit{itemCount === 1 ? '' : 's'} total
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  {!needsPayment && (
                  <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => downloadReceipt(o.order_id)}
                      disabled={busy === `receipt-${o.order_id}`}
                      data-testid={`download-receipt-${o.order_id}`}
                    >
                      {busy === `receipt-${o.order_id}` ? (
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                      ) : (
                        <FileText size={14} className="mr-1.5" />
                      )}
                      Receipt PDF
                    </Button>
                    {!o.is_special_order && (
                    <Button
                      size="sm"
                      onClick={() => buyAgain(o.order_id)}
                      disabled={busy === `reorder-${o.order_id}`}
                      data-testid={`buy-again-${o.order_id}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {busy === `reorder-${o.order_id}` ? (
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                      ) : (
                        <RefreshCcw size={14} className="mr-1.5" />
                      )}
                      Buy again
                    </Button>
                    )}
                  </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
